import type { InviteRole } from '@echo/core';
import { setReadyInviteRole, useReadyInvites } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  createFileRoute,
  Navigate,
  useCanGoBack,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useId, useState } from 'react';
import { AccessChoice, useChoiceLabel, useRoleDescriptions } from '@/components/access-choice';
import { ToolbarBack } from '@/components/empty-state-frame';
import { InfoIcon } from '@/components/info-icon';

const INVITE_ROLES: readonly InviteRole[] = ['member', 'leader'];

interface AccessSearch {
  inviteId?: string;
}

export const Route = createFileRoute('/_app/account_/groups_/$id_/invite_/access')({
  validateSearch: (search: Record<string, unknown>): AccessSearch => ({
    inviteId: typeof search.inviteId === 'string' ? search.inviteId : undefined,
  }),
  component: InviteAccessPage,
});

/**
 * Invite people › Invite as (Figma account/access-level): Member or Group leader for someone
 * on Ready to invite, and why Facilitator is not offered. Save sets it on their invite.
 */
function InviteAccessPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const description = useRoleDescriptions();
  const label = useChoiceLabel();
  const question = useId();
  const { id } = Route.useParams();
  const { inviteId } = Route.useSearch();
  const invite = useReadyInvites(id).find((candidate) => candidate.id === inviteId);
  const [chosen, setChosen] = useState<InviteRole>(invite?.role ?? 'member');
  // Ready to invite lasts the visit: after a reload, or from a stale link, it has no such person.
  if (!invite) return <Navigate to="/account/groups/$id/invite" params={{ id }} replace />;

  const back = () =>
    canGoBack
      ? router.history.back()
      : void navigate({ to: '/account/groups/$id/invite', params: { id }, replace: true });
  return (
    <>
      <ToolbarBack
        render={({ className, children }) => (
          <button type="button" className={className} onClick={back}>
            {children}
          </button>
        )}
      />
      <h1 className="text-display-sm font-bold">
        <Trans>Access level</Trans>
      </h1>
      <div className="flex flex-col gap-section-sm pt-content-md">
        <div
          role="radiogroup"
          aria-labelledby={question}
          className="flex flex-col gap-content-sm rounded-xl border border-border-weak bg-surface p-content-sm"
        >
          <p id={question} className="text-body-md">
            <Trans>Choose what this person can do in the group.</Trans>
          </p>
          <div className="flex flex-col">
            {INVITE_ROLES.map((role) => (
              <AccessChoice
                key={role}
                name="invite-role"
                label={label(role)}
                description={description[role]}
                selected={chosen === role}
                onPress={() => setChosen(role)}
              />
            ))}
          </div>
        </div>
        <p className="flex gap-internal-md text-body-md text-on-surface-medium">
          <InfoIcon />
          <Trans>
            You can't make someone a Facilitator. Leaders can choose this role themselves in their
            own Account settings.
          </Trans>
        </p>
        <div className="flex flex-wrap justify-end gap-content-sm">
          <Button variant="ghost" onClick={back}>
            {t`Cancel`}
          </Button>
          <Button
            onClick={() => {
              setReadyInviteRole(id, invite.id, chosen);
              back();
            }}
          >
            {t`Save`}
          </Button>
        </div>
      </div>
    </>
  );
}
